import PolicyLayout from './PolicyLayout';

export default function PrivacyPolicyPage() {
  return (
    <PolicyLayout
      title="Privacy Policy"
      subtitle="How DotCall collects, uses, and protects your information."
    >
      <p>
        DotCall ("we", "our", "us") is committed to protecting the privacy of users of our AI voice agent
        platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information
        when you use our website, dashboard, APIs, and related services (collectively, the "Services").
      </p>
      <p>
        By accessing or using DotCall, you agree to the collection and use of information in accordance with
        this Policy. This Policy is published in accordance with the provisions of the Information Technology
        Act, 2000 and the Information Technology (Reasonable Security Practices and Procedures and Sensitive
        Personal Data or Information) Rules, 2011 of India.
      </p>

      <h2>1. Information We Collect</h2>
      <h3>a. Account information</h3>
      <p>When you create an account, we collect your name, email address, phone number, company name, and
        authentication credentials (hashed password or OAuth identifier).</p>
      <h3>b. Call and voice data</h3>
      <p>When AI voice calls are made or received through the platform, we process phone numbers, call
        duration, call timestamps, call recordings (when enabled), transcripts, and call outcomes to provide
        and improve the Services.</p>
      <h3>c. Usage data</h3>
      <p>We automatically collect information about how you interact with the Services, including IP address,
        browser type, device identifiers, pages visited, and feature usage.</p>
      <h3>d. Payment data</h3>
      <p>Payments are processed through our PCI-DSS compliant payment partner (Razorpay). We do not store
        full card numbers on our servers; we retain only transaction metadata such as payment ID, amount,
        currency, and status.</p>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To create and manage your account and provide the AI voice agent Services.</li>
        <li>To connect and bridge voice calls via telephony providers (Twilio) and AI providers (OpenAI Realtime API).</li>
        <li>To process payments, issue invoices, and manage wallet balances.</li>
        <li>To send service-related notifications, usage alerts, and product updates.</li>
        <li>To improve accuracy, latency, and quality of our AI voice models and knowledge-base retrieval.</li>
        <li>To detect, prevent, and address fraud, abuse, security, or technical issues.</li>
        <li>To comply with legal obligations, lawful requests, and enforce our Terms &amp; Conditions.</li>
      </ul>

      <h2>3. Sharing &amp; Disclosure</h2>
      <p>We do not sell your personal information. We share information only with:</p>
      <ul>
        <li><strong>Service providers</strong> — Twilio (telephony), OpenAI (AI inference), Razorpay (payments),
          MongoDB Atlas (database hosting), cloud infrastructure providers, and email delivery partners — strictly
          under contractual data-protection obligations.</li>
        <li><strong>Integrations you enable</strong> — such as Shopify, WooCommerce, HubSpot, Google Sheets, or
          custom webhooks — to deliver the features you request.</li>
        <li><strong>Legal authorities</strong> — when required by Indian or applicable law, court order, or to
          protect the rights, safety, and property of DotCall or our users.</li>
      </ul>

      <h2>4. Call Recordings &amp; Consent</h2>
      <p>You are solely responsible for obtaining all consents required under applicable telecom and privacy
        regulations (including TRAI regulations in India) before using DotCall to make outbound calls, record
        conversations, or process customer voice data. We strongly recommend agents disclose at the start of a
        call that the call may be recorded for quality and training purposes.</p>

      <h2>5. Data Retention</h2>
      <p>We retain your account and call data for as long as your account is active or as needed to provide the
        Services. Call recordings and transcripts are retained for up to 12 months by default unless you
        configure a shorter retention period or request earlier deletion. Billing and tax records are retained
        as required under Indian law (typically 8 years).</p>

      <h2>6. Security</h2>
      <p>We implement reasonable administrative, technical, and physical safeguards including encryption in
        transit (TLS 1.2+), encrypted storage of credentials and API keys, JWT-based authentication, role-based
        access control, and periodic security reviews. No method of transmission over the internet is 100%
        secure, and we cannot guarantee absolute security.</p>

      <h2>7. Your Rights</h2>
      <p>Subject to applicable law, you have the right to:</p>
      <ul>
        <li>Access, correct, or update your personal information from your dashboard settings.</li>
        <li>Request a copy of your data in a portable format.</li>
        <li>Request deletion of your account and associated data (subject to legal retention requirements).</li>
        <li>Withdraw consent for optional processing at any time.</li>
      </ul>
      <p>To exercise any of these rights, contact us at <strong>support@dotcall.io</strong>.</p>

      <h2>8. Cookies</h2>
      <p>We use essential cookies for authentication and session management. We do not use tracking cookies for
        advertising. You may disable cookies in your browser, but certain features of the Services may not
        function properly as a result.</p>

      <h2>9. Children</h2>
      <p>The Services are not intended for users under 18 years of age. We do not knowingly collect personal
        information from children.</p>

      <h2>10. Changes to This Policy</h2>
      <p>We may update this Policy from time to time. Material changes will be notified via email or a prominent
        dashboard notice at least 7 days before they take effect.</p>

      <h2>11. Grievance Officer</h2>
      <p>In accordance with the Information Technology Act, 2000 and rules thereunder, you may contact our
        Grievance Officer for any complaints or concerns:</p>
      <p>
        <strong>Email:</strong> support@dotcall.io<br />
        <strong>Phone:</strong> +91 88798 51335<br />
        <strong>Response time:</strong> Within 15 days of receipt.
      </p>
    </PolicyLayout>
  );
}
